import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { Divider, CloseOutlined, Skeleton } from "src/assets/imports/antdComponents";
import { ITranscriptViewProps } from "../../models/interfaces";
import { RootState } from "src/store";
import "./style.css";

const TranscriptView: React.FC<ITranscriptViewProps> = ({
	transcriptName,
	createdBy,
	transcriptItems,
	onClose,
	isLoadingTranscript,
	contextIds,
	isExpanded,
	handleExpand,
	module,
}) => {
	const { t } = useTranslation();
	const [currentMentionIndex, setCurrentMentionIndex] = useState(0);
	const [highlightedItems, setHighlightedItems] = useState<any[]>([]);
	const transcriptBodyRef = useRef<HTMLDivElement>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);
	const isManualNavigationRef = useRef(false);
	const { isCountWidgetOpen } = useSelector((state: RootState) => state.countWidgetSlice);

	const scrollToItem = (element: HTMLElement) => {
		if (transcriptBodyRef.current) {
			transcriptBodyRef.current.scrollTo({ top: element.offsetTop, behavior: "smooth" });
		}
	};

	useEffect(() => {
		if (!isLoadingTranscript && transcriptItems && contextIds) {
			const matchedItems = transcriptItems?.filter((item) => contextIds?.includes(item?.dialog_id));
			setHighlightedItems(matchedItems);
			setCurrentMentionIndex(0);
			if (matchedItems.length > 0) {
				const firstHighlightedItem = matchedItems[0];
				if (firstHighlightedItem) {
					setTimeout(() => {
						const element = document?.getElementById(`transcript-item-${firstHighlightedItem?.dialog_id}`);
						if (element) {
							scrollToItem(element);
						}
					}, 100);
				}
			}
		}
	}, [isLoadingTranscript, transcriptItems, contextIds]);

	const updateCurrentMentionOnScroll = useCallback(() => {
		// Only perform scroll calculations when count widget is open and not manually navigating
		if (!isCountWidgetOpen || !transcriptBodyRef.current || highlightedItems.length === 0 || isManualNavigationRef.current) return;

		const containerRect = transcriptBodyRef.current.getBoundingClientRect();
		const containerTop = containerRect.top;

		let mostVisibleItem = null;
		let maxVisibleArea = 0;

		for (let i = 0; i < highlightedItems.length; i++) {
			const item = highlightedItems[i];
			const element = document.getElementById(`transcript-item-${item.dialog_id}`);

			if (element) {
				const rect = element.getBoundingClientRect();

				const visibleTop = Math.max(rect.top, containerTop);
				const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
				const visibleHeight = Math.max(0, visibleBottom - visibleTop);
				const visibleArea = visibleHeight * rect.width;

				const topBias = 1 - (rect.top - containerTop) / containerRect.height;
				const weightedVisibleArea = visibleArea * (1 + topBias);

				if (visibleArea > 0 && weightedVisibleArea > maxVisibleArea) {
					maxVisibleArea = weightedVisibleArea;
					mostVisibleItem = i;
				}
			}
		}

		if (mostVisibleItem !== null && mostVisibleItem !== currentMentionIndex) {
			setCurrentMentionIndex(mostVisibleItem);
		}
	}, [highlightedItems, currentMentionIndex, isCountWidgetOpen]);

	// Set up scroll event listener only when count widget is open
	useEffect(() => {
		const transcriptBody = transcriptBodyRef.current;
		if (!transcriptBody || highlightedItems.length === 0 || !isCountWidgetOpen) return;

		// Add scroll event listener
		transcriptBody.addEventListener("scroll", updateCurrentMentionOnScroll);

		// Initial check
		updateCurrentMentionOnScroll();

		return () => {
			transcriptBody.removeEventListener("scroll", updateCurrentMentionOnScroll);
		};
	}, [highlightedItems, updateCurrentMentionOnScroll, isCountWidgetOpen]);

	const handlePreviousMention = () => {
		if (currentMentionIndex > 0) {
			const newIndex = currentMentionIndex - 1;
			// Set manual navigation flag
			isManualNavigationRef.current = true;
			setCurrentMentionIndex(newIndex);

			// Scroll to the previous mention
			const item = highlightedItems[newIndex];
			if (item) {
				const element = document?.getElementById(`transcript-item-${item?.dialog_id}`);
				if (element) {
					scrollToItem(element);
					setTimeout(() => {
						isManualNavigationRef.current = false;
					}, 800);
				}
			}
		}
	};

	const handleNextMention = () => {
		if (currentMentionIndex < highlightedItems.length - 1) {
			const newIndex = currentMentionIndex + 1;
			isManualNavigationRef.current = true;
			setCurrentMentionIndex(newIndex);

			const item = highlightedItems[newIndex];
			if (item) {
				const element = document?.getElementById(`transcript-item-${item?.dialog_id}`);
				if (element) {
					scrollToItem(element);
					setTimeout(() => {
						isManualNavigationRef.current = false;
					}, 800);
				}
			}
		}
	};

	return (
		<div className="transcript-view relative">
			<div className="transcript-header bg-[--drawer-header-bg]">
				<div className="transcript-info">
					<div className="flex items-center justify-start gap-1">
						<div className="text-[--secondary-color] text-base-14 ml-1">{t("References")}</div>
					</div>
				</div>
				<i className="material-symbols-outlined text-[1.5rem] cursor-pointer text-[--secondary-color] absolute right-16 mr-4" onClick={handleExpand}>
					{isExpanded ? "hide" : "expand_content"}
				</i>
				<CloseOutlined onClick={onClose} className="secondary-color p-2 text-base" />
			</div>
			<div className="flex items-center mb-2 mt-4 ml-6">
				<i className="material-symbols-outlined text-2xl text-[--secondary-color] cursor-pointer" onClick={onClose}>
					keyboard_arrow_left
				</i>
				<div className="font-semibold text-base-1 line-clamp-2 truncate overflow-hidden whitespace-nowrap text-ellipsis secondary-color">{transcriptName}</div>
			</div>

			{/* Floating navigation bar for mentions - only shown when count widget is open */}
			{isCountWidgetOpen && highlightedItems.length > 1 && (
				<div className="mention-navigation-floating">
					<div className="mention-navigation-label">
						{currentMentionIndex + 1} / {highlightedItems.length}
					</div>
					<Divider type="vertical" className="mention-divider" />
					<button
						onClick={handlePreviousMention}
						disabled={currentMentionIndex === 0}
						className={`mention-nav-button ${currentMentionIndex === 0 ? "mention-nav-disabled" : ""}`}
						aria-label="Previous mention">
						<i className="material-symbols-outlined">keyboard_arrow_up</i>
					</button>
					<button
						onClick={handleNextMention}
						disabled={currentMentionIndex === highlightedItems.length - 1}
						className={`mention-nav-button ${currentMentionIndex === highlightedItems.length - 1 ? "mention-nav-disabled" : ""}`}
						aria-label="Next mention">
						<i className="material-symbols-outlined">keyboard_arrow_down</i>
					</button>
				</div>
			)}

			<div className="transcript-body" ref={transcriptBodyRef}>
				{isLoadingTranscript ? (
					Array?.from({ length: 3 })?.map((item) => (
						<>
							<Skeleton />
							<Divider className="my-6" />
						</>
					))
				) : transcriptItems?.length > 0 ? (
					transcriptItems?.map((item, index) => {
						const isHighlighted = contextIds?.includes(item?.dialog_id);
						return (
							<>
								<div key={index} id={`transcript-item-${item?.dialog_id}`} className="transcript-item">
									<p className="transcript-drawer-text text-base-1 flex gap-3 flex-col">
										<p className="transcript-speaker font-semibold text-[--secondary-color-2]">
											{item?.speaker_type}
											{module && module === "Insights" && ` (${item?.speaker_type?.charAt(0)?.toUpperCase()})`}
										</p>

										<p className={`font-normal ${isHighlighted ? "highlight-container" : ""}`}>
											<span className={`text-[--neutral-grey] font-normal break-words ${isHighlighted ? "highlight-block" : ""}`}>{item?.dialog}</span>
										</p>
									</p>
								</div>
								<Divider className="my-4" />
							</>
						);
					})
				) : (
					<div className="no-transcript text-[x-small]">{t("No transcript available")}</div>
				)}
			</div>
		</div>
	);
};

export default React.memo(TranscriptView);
